import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, ChevronLeft, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const categories = [
  { value: 'order', label: 'Вопрос по заказу' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'payment', label: 'Оплата' },
  { value: 'product', label: 'Качество продукта' },
  { value: 'general', label: 'Общий вопрос' },
  { value: 'other', label: 'Другое' }
];

const statusLabels = {
  open: { label: 'Открыт', color: 'text-blue-600 bg-blue-100' },
  in_progress: { label: 'В работе', color: 'text-yellow-600 bg-yellow-100' },
  waiting_customer: { label: 'Ожидает ответа', color: 'text-orange-600 bg-orange-100' },
  resolved: { label: 'Решён', color: 'text-green-600 bg-green-100' },
  closed: { label: 'Закрыт', color: 'text-gray-600 bg-gray-100' }
};

export default function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('home'); // home, list, new, detail
  const [chatMode, setChatMode] = useState('ticket'); // 'live' | 'ticket' — selected on home view
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Form state
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  
  // Guest form
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  
  const isLoggedIn = !!localStorage.getItem('token');
  
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      fetchTickets();
    }
  }, [isOpen, isLoggedIn]);
  
  // Live polling — when ticket detail is open and not closed, refresh every 6s
  useEffect(() => {
    if (view !== 'detail' || !selectedTicket || selectedTicket.status === 'closed') return;
    const interval = setInterval(async () => {
      try {
        const url = isLoggedIn
          ? `${API}/support/tickets/${selectedTicket.id}`
          : `${API}/support/guest/tickets/${selectedTicket.id}`;
        const response = await axios.get(url, { headers: getAuthHeaders() });
        if ((response.data.messages?.length || 0) !== (selectedTicket.messages?.length || 0)
            || response.data.status !== selectedTicket.status) {
          setSelectedTicket(response.data);
        }
      } catch (e) { /* silent — polling */ }
    }, 6000);
    return () => clearInterval(interval);
  }, [view, selectedTicket, isLoggedIn]);
  
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/support/my-tickets`, {
        headers: getAuthHeaders()
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTicketDetails = async (ticketId) => {
    setLoading(true);
    try {
      // For guests use the public guest endpoint; for logged-in users use the authenticated one
      const url = isLoggedIn
        ? `${API}/support/tickets/${ticketId}`
        : `${API}/support/guest/tickets/${ticketId}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setSelectedTicket(response.data);
      setView('detail');
    } catch (error) {
      console.error('Error fetching ticket:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    const isLive = chatMode === 'live';
    // For live mode, message is the primary input; subject is auto-generated
    const finalSubject = isLive ? `Чат: ${message.trim().slice(0, 40)}${message.length > 40 ? '…' : ''}` : subject.trim();
    if (!message.trim()) return;
    if (!isLive && !finalSubject) return;
    if (!isLoggedIn) {
      if (isLive && !guestPhone.trim()) return;
      if (!isLive && !guestEmail.trim()) return;
    }
    
    setSending(true);
    try {
      const data = {
        subject: finalSubject || 'Live chat',
        category: isLive ? 'general' : category,
        message,
        ...(isLoggedIn ? {} : {
          name: guestName,
          email: guestEmail,
          phone: guestPhone
        })
      };
      
      const response = await axios.post(`${API}/support/tickets`, data, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        // Reset form
        setSubject('');
        setCategory('general');
        setMessage('');
        
        // For LIVE mode: open the new ticket detail immediately so user can chat
        if (isLive) {
          await fetchTicketDetails(response.data.ticket.id);
        } else if (isLoggedIn) {
          await fetchTickets();
          setView('list');
          alert(`Ваше обращение #${response.data.ticket.ticket_number} создано! Ответ придёт на ваш email${guestEmail ? ' ('+guestEmail+')' : ''}.`);
        } else {
          alert(`Ваше обращение #${response.data.ticket.ticket_number} создано! Ответ придёт на email ${guestEmail}.`);
          setGuestName(''); setGuestEmail(''); setGuestPhone('');
          setIsOpen(false);
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Ошибка при создании обращения');
    } finally {
      setSending(false);
    }
  };
  
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      const url = isLoggedIn
        ? `${API}/support/tickets/${selectedTicket.id}/reply`
        : `${API}/support/guest/tickets/${selectedTicket.id}/reply`;
      const response = await axios.post(url, { message: replyMessage }, { headers: getAuthHeaders() });
      
      if (response.data.success) {
        setReplyMessage('');
        // Refresh ticket details
        await fetchTicketDetails(selectedTicket.id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Don't show on admin pages
  if (window.location.pathname.includes('/admin')) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-[9998] w-14 h-14 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 transition-all duration-300 flex items-center justify-center ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="support-widget-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
      
      {/* Widget Panel */}
      <div className={`fixed bottom-6 right-6 z-[9999] w-96 max-h-[600px] bg-white rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {view !== 'home' && (
              <button onClick={() => { setView('home'); setSelectedTicket(null); }} className="hover:bg-white/20 rounded-full p-1" data-testid="support-back-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <MessageCircle className="w-5 h-5" />
            <span className="font-semibold">
              {view === 'home' ? 'Поддержка Айнанан' :
               view === 'list' ? 'Мои обращения' :
               view === 'new' ? (chatMode === 'live' ? 'Чат с оператором' : 'Новое обращение') :
               'Диалог'}
            </span>
          </div>
          <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="max-h-[500px] overflow-y-auto">
          {/* Home View — mode selector */}
          {view === 'home' && (
            <div className="p-4 space-y-3" data-testid="support-home">
              <p className="text-sm text-gray-600 mb-2">Как мы можем помочь?</p>
              <button
                onClick={() => { setChatMode('live'); setView('new'); }}
                className="w-full p-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all text-left flex items-start gap-3 shadow-sm"
                data-testid="support-live-btn"
              >
                <span className="text-2xl">💬</span>
                <div className="flex-1">
                  <p className="font-semibold">Чат с оператором</p>
                  <p className="text-xs opacity-90 mt-0.5">Диалог в реальном времени</p>
                </div>
              </button>
              <button
                onClick={() => { setChatMode('ticket'); setView('new'); }}
                className="w-full p-4 bg-white border-2 border-indigo-200 hover:border-indigo-400 text-gray-900 rounded-xl transition-all text-left flex items-start gap-3"
                data-testid="support-ticket-btn"
              >
                <span className="text-2xl">📨</span>
                <div className="flex-1">
                  <p className="font-semibold">Отправить запрос</p>
                  <p className="text-xs text-gray-500 mt-0.5">Ответ придёт на email</p>
                </div>
              </button>
              {isLoggedIn && (
                <button
                  onClick={() => setView('list')}
                  className="w-full p-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all text-left flex items-center gap-3"
                  data-testid="support-history-btn"
                >
                  <span className="text-xl">📋</span>
                  <span className="text-sm font-medium">Мои обращения</span>
                </button>
              )}
            </div>
          )}
          {/* List View */}
          {view === 'list' && (
            <div className="p-4">
              <button
                onClick={() => setView('new')}
                className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium mb-4"
              >
                + Новое обращение
              </button>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : !isLoggedIn ? (
                <div className="text-center py-6 text-gray-500">
                  <p className="mb-2">Войдите, чтобы видеть историю обращений</p>
                  <button
                    onClick={() => setView('new')}
                    className="text-indigo-600 hover:underline"
                  >
                    или создайте обращение без входа
                  </button>
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <p>У вас пока нет обращений</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map(ticket => (
                    <button
                      key={ticket.id}
                      onClick={() => fetchTicketDetails(ticket.id)}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{ticket.subject}</p>
                          <p className="text-xs text-gray-500">{ticket.ticket_number}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${statusLabels[ticket.status]?.color || 'bg-gray-100'}`}>
                          {statusLabels[ticket.status]?.label || ticket.status}
                        </span>
                      </div>
                      {ticket.last_message && (
                        <p className="text-sm text-gray-600 mt-2 truncate">
                          {ticket.last_message.sender_type === 'admin' ? '👤 Поддержка: ' : 'Вы: '}
                          {ticket.last_message.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(ticket.updated_at || ticket.created_at)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* New Ticket Form */}
          {view === 'new' && (
            <form onSubmit={handleSubmitTicket} className="p-4 space-y-4">
              {chatMode === 'live' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                  <span className="text-xl">💬</span>
                  <p className="text-xs text-emerald-800">Вы общаетесь с оператором. Ответ обычно в течение нескольких минут в рабочие часы.</p>
                </div>
              )}
              {!isLoggedIn && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ваше имя</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Как к вам обращаться?"
                    />
                  </div>
                  {/* For live chat: phone is enough. For ticket mode: email is required for reply. */}
                  {chatMode !== 'live' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Для ответа от поддержки"
                        required
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Телефон {chatMode === 'live' ? '*' : ''}</label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="+7 (___) ___-__-__"
                      required={chatMode === 'live'}
                    />
                  </div>
                </>
              )}
              
              {chatMode !== 'live' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Категория</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Тема *</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Кратко опишите проблему"
                      required
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{chatMode === 'live' ? 'Ваше сообщение *' : 'Сообщение *'}</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={chatMode === 'live' ? 3 : 4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  placeholder={chatMode === 'live' ? 'Напишите ваш вопрос, оператор подключится...' : 'Подробно опишите вашу проблему или вопрос...'}
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={sending}
                className={`w-full py-3 rounded-xl transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-white ${chatMode === 'live' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                data-testid="support-submit-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Отправка...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {chatMode === 'live' ? 'Начать чат' : 'Отправить запрос'}
                  </>
                )}
              </button>
            </form>
          )}
          
          {/* Ticket Detail View */}
          {view === 'detail' && selectedTicket && (
            <div className="flex flex-col h-[500px]">
              {/* Ticket Info */}
              <div className="p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900">{selectedTicket.subject}</p>
                    <p className="text-xs text-gray-500">{selectedTicket.ticket_number}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusLabels[selectedTicket.status]?.color || 'bg-gray-100'}`}>
                    {statusLabels[selectedTicket.status]?.label || selectedTicket.status}
                  </span>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                ) : (
                  selectedTicket.messages?.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-xl ${
                          msg.sender_type === 'customer'
                            ? 'bg-indigo-600 text-white rounded-br-none'
                            : 'bg-gray-100 text-gray-900 rounded-bl-none'
                        }`}
                      >
                        {msg.sender_type !== 'customer' && (
                          <p className="text-xs font-medium mb-1 opacity-70">
                            {msg.sender_name || 'Поддержка'}
                          </p>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'customer' ? 'text-indigo-200' : 'text-gray-400'}`}>
                          {formatDate(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <form onSubmit={handleSendReply} className="p-4 border-t border-gray-100 bg-white">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Введите сообщение..."
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <button
                      type="submit"
                      disabled={sending || !replyMessage.trim()}
                      className="w-10 h-10 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </form>
              )}
              
              {selectedTicket.status === 'closed' && (
                <div className="p-4 bg-gray-50 text-center text-gray-500 text-sm">
                  <CheckCircle className="w-5 h-5 inline mr-1" />
                  Обращение закрыто
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
